import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Divider, Stack, Text, Accordion } from "@bookingcom/bui-react";
import useFormatPrice from "../../../../hooks/useFormatPrice.js";
import { useBreakdownController } from "./useBreakdown.controller.js";
import styles from "./Breakdown.module.css";
import { usePriceConsistencyMonitoringEvents } from "../../../../hooks/c360/usePriceConsistencyMonitoringEvents.js";
import { isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import { getAttachedAncillaries, isNegativePrice, sortSectionItems } from "../helpers/index.js";
import { actions as ariaLiveActions } from "../../../../store/ariaLive/actions.js";
import { t } from "@bookingcom/lingojs-core";
import { useDispatch } from "react-redux";
import { useI18n } from "@bookingcom/lingojs-react";
import usePrevious from "../../../../hooks/usePrevious.js";
function getAbsolutePrice(price) {
    return { ...price, units: Math.abs(price.units), nanos: Math.abs(price.nanos) };
}
function SectionItems({ items, mixin }) {
    const { formatPrice } = useFormatPrice();
    return useMemo(() => items.map(({ id, items, price, title }, idx) => {
        const negative = isNegativePrice(price);
        const titleAriaLabel = title.endsWith("*") ? title.slice(0, -1) : undefined;
        const priceAriaLabel = negative ? formatPrice(getAbsolutePrice(price)) : undefined;
        return (React.createElement(Stack, { mixin: mixin, gap: 1, key: `${id}-${idx}` },
            React.createElement(Stack, { gap: 2, direction: "row" },
                React.createElement(Stack.Item, { grow: true },
                    React.createElement(Text, { variant: "small_1", color: "neutral", attributes: {
                            "data-testid": `upb_title-${id}`,
                            ...(titleAriaLabel && { "aria-label": titleAriaLabel })
                        } }, title)),
                React.createElement(Text, { variant: "small_1", color: negative ? "constructive" : "neutral", attributes: {
                        "data-testid": `upb_price-${id}`,
                        ...(priceAriaLabel && { "aria-label": priceAriaLabel })
                    } }, formatPrice(price))),
            items.length ? React.createElement(SectionItems, { mixin: { paddingInlineStart: 2 }, items: items }) : undefined));
    }), [items, mixin, formatPrice]);
}
function PriceItemTitle({ item }) {
    const { formatPrice } = useFormatPrice();
    const negative = isNegativePrice(item.price);
    const titleAriaLabel = item.title.endsWith("*") ? item.title.slice(0, -1) : undefined;
    const priceAriaLabel = negative ? formatPrice(getAbsolutePrice(item.price)) : undefined;
    return (React.createElement(Stack, { gap: 0.5 },
        React.createElement(Stack, { gap: 0, direction: "row" },
            React.createElement(Stack.Item, { grow: true },
                React.createElement(Text, { variant: "body_2", attributes: {
                        "data-testid": `upb_title-${item.id}`,
                        ...(titleAriaLabel && { "aria-label": titleAriaLabel })
                    } }, item.title)),
            React.createElement(Text, { variant: "body_2", color: negative ? "constructive" : "neutral", attributes: {
                    "data-testid": `upb_price-${item.id}`,
                    ...(priceAriaLabel && { "aria-label": priceAriaLabel })
                } }, formatPrice(item.price))),
        item.subtitle && (React.createElement(Text, { variant: "small_1", color: "neutral_alt", attributes: { "data-testid": `upb_subtitle-${item.id}` } }, item.subtitle))));
}
function PriceLineItem({ item }) {
    return (React.createElement(Stack, { className: styles.priceLineItem, mixin: { paddingInlineEnd: 10 } },
        React.createElement(PriceItemTitle, { item: item })));
}
function AccordionItem({ item, expandedByDefault }) {
    const [active, setActive] = useState(expandedByDefault);
    return (React.createElement(Accordion, { active: active, className: styles.accordion, onClose: () => setActive(false), onOpen: () => setActive(true), titleContent: React.createElement(PriceItemTitle, { item: item }), attributes: { disabled: !item.items.length } }, item.items.length ? (React.createElement(Stack, { gap: 1, mixin: { paddingBlockStart: 2, paddingBlockEnd: 1 } },
        React.createElement(SectionItems, { mixin: { paddingInlineEnd: 10 }, items: item.items }))) : undefined));
}
function AccordionGroup({ section, sectionFooter, isLast }) {
    return (React.createElement(React.Fragment, null,
        React.createElement(Stack, { gap: 2 },
            React.createElement(Text, { variant: "strong_2", tagName: "h3", attributes: { "data-testid": `breakdown_section_${section.id}` } }, section.name),
            useMemo(() => sortSectionItems(section.id, section.items).map((item, idx) => {
                if (isFeatureRunningClientSide("FLIGHTS_STRATEGY_MANAGER_REPLACE_ACCORDION_IF_NO_BREAKDOWN_ITEM_IN_UPB") &&
                    !item.items.length) {
                    return React.createElement(PriceLineItem, { item: item, key: `${item.id}-${idx}` });
                }
                return (React.createElement(AccordionItem, { item: item, key: `${item.id}-${idx}`, expandedByDefault: section.expandedByDefault }));
            }), [section.items, section.id, section.expandedByDefault])),
        sectionFooter !== undefined ? React.createElement(Stack.Item, null, sectionFooter) : undefined,
        !isLast ? React.createElement(Divider, null) : null));
}
export function Breakdown(props) {
    const { totalPrice, totalPriceTitle, footerSections = {}, sections = {}, totalPriceFooter, isSummary } = useBreakdownController(props);
    const i18n = useI18n();
    const { id: productId, price, items, addedItems } = props;
    const { formatPrice } = useFormatPrice();
    const trackPriceConsistencyMonitoringEvents = usePriceConsistencyMonitoringEvents();
    const dispatch = useDispatch();
    const sortedIds = useMemo(() => ["FLIGHT", "EXTRA", "BOOKING_CHANGES", "FEE", "DISCOUNT"], []);
    const sortedSections = useMemo(() => sortedIds.map((id) => sections?.[id]).filter((section) => Boolean(section)), [sections, sortedIds]);
    const formattedTotalPrice = formatPrice(totalPrice);
    const prevFormattedTotalPrice = usePrevious(formattedTotalPrice);
    useEffect(() => {
        if (formattedTotalPrice !== prevFormattedTotalPrice) {
            dispatch(ariaLiveActions.setMessage({
                message: i18n.trans(t("a11y_flights_aria_live_checkout_new_total_price", {
                    variables: { price: formattedTotalPrice }
                })),
                type: "polite"
            }));
        }
    }, [dispatch, formattedTotalPrice, i18n, prevFormattedTotalPrice]);
    const accordionItems = useMemo(() => sortedSections.map((section, idx) => (React.createElement(AccordionGroup, { key: `${section.id}-${idx}`, section: section, sectionFooter: footerSections?.[section.id], isLast: idx === sortedSections.length - 1 }))), [sortedSections, footerSections]);
    const flattenUnifiedPriceItems = useCallback((items) => items.flatMap((item) => [
        { productType: item.id, productPrice: item.price },
        ...flattenUnifiedPriceItems(item.items)
    ]), []);
    const attachedAncillaries = useMemo(() => getAttachedAncillaries({ id: productId, price, items, addedItems }), [productId, price, items, addedItems]);
    useEffect(() => {
        if (productId && (items.length || addedItems.length)) {
            const productPrices = flattenUnifiedPriceItems([...items, ...addedItems]);
            trackPriceConsistencyMonitoringEvents("BREAKDOWN", productId, productPrices, totalPrice, undefined, attachedAncillaries);
        }
    }, [
        addedItems,
        flattenUnifiedPriceItems,
        items,
        productId,
        totalPrice,
        trackPriceConsistencyMonitoringEvents,
        attachedAncillaries
    ]);
    return (React.createElement(Stack, { direction: "column", gap: 2, attributes: { "data-testid": "upb_container" } },
        !isSummary && (React.createElement(React.Fragment, null,
            React.createElement(Stack, { gap: 2 }, accordionItems),
            React.createElement(Divider, null))),
        React.createElement(Stack, { gap: 0, mixin: !isSummary ? { paddingInlineEnd: 10 } : {} },
            React.createElement(Stack, { direction: "row", gap: 0 },
                React.createElement(Stack.Item, { grow: true },
                    React.createElement(Text, { variant: !isSummary ? "headline_3" : "strong_1", tagName: "h2", attributes: { "data-testid": "upb_title-total" } }, totalPriceTitle)),
                React.createElement(Stack, { direction: "column", gap: 0 },
                    React.createElement(Text, { variant: !isSummary ? "headline_3" : "strong_1", attributes: { "data-testid": "upb_price-total" } }, formattedTotalPrice))),
            React.createElement(Text, { variant: "body_2", className: styles.totalPriceFooter }, totalPriceFooter))));
}
